import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { TaskDetailView } from '@/components/tasks/TaskDetailView';
import { tasksService } from '@/services/tasks.service';
import type { Task } from '@/types';

function TaskDetailContent() {
  const { id } = useParams<{ id: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadTask();
    }
  }, [id]);

  const loadTask = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await tasksService.getTask(id);
      setTask(data);
    } catch (err: any) {
      console.error('Error loading task:', err);
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flowky-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading task...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !task) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Task not found</h2>
          {error && <p className="text-gray-600 mb-4">{error}</p>}
          <Link to="/tasks" className="text-flowky-primary hover:underline">
            Back to tasks
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Tasks', href: '/tasks' }, { label: task.title }]} />
      <TaskDetailView task={task} />
    </AppLayout>
  );
}

export default function TaskDetailPage() {
  return (
    <ProtectedRoute>
      <TaskDetailContent />
    </ProtectedRoute>
  );
}
